/**
 * The staff handbook (STAFF_GUIDE_PLAN.md): written explanations of how the platform works, per desk
 * staff role. Content lives in ./sections (one file per section); this file puts it in order and
 * holds the helpers the web app and the API share (role filter, placeholders, text parsing).
 */
import { fillVars } from '../academy/index.js';
import { admin } from './sections/admin.js';
import { basics } from './sections/basics.js';
import { desk } from './sections/desk.js';
import { lifecycle } from './sections/lifecycle.js';
import { masters } from './sections/masters.js';
import { products } from './sections/products.js';
import { risk } from './sections/risk.js';
import { trading } from './sections/trading.js';
import { parseGuideRows } from './vars.js';
export * from './types.js';
export * from './vars.js';
/**
 * Every article, in section order (GUIDE_SECTIONS). Marked pure so the web bundle, which gets its
 * articles from the API and never imports this list, leaves the content out.
 */
export const GUIDE_ARTICLES = /* @__PURE__ */ [].concat(basics, trading, desk, masters, risk, lifecycle, products, admin);
export const guideArticle = (id) => GUIDE_ARTICLES.find((a) => a.id === id) ?? null;
/**
 * The articles a staff role may read. Admin reads everything. Every other role reads the articles
 * listed for it, never Admin settings; a retention dealer never reads Master accounts (they have no
 * masters). An article tagged with a desk permission shows only when the role's act on it is request
 * or direct: pass the role's acts, a missing one reads as none.
 */
export function guideFor(role, acts = {}, articles = GUIDE_ARTICLES) {
    if (role === 'admin')
        return [...articles];
    return articles.filter((a) => a.section !== 'admin' &&
        !(role === 'retention_dealer' && a.section === 'masters') &&
        a.roles.includes(role) &&
        (!a.perm || acts[a.perm] === 'request' || acts[a.perm] === 'direct'));
}
/** A table's generated rows (`rowsFrom`), each cut or padded to the head's length. */
const generatedRows = (b, vars) => b.rowsFrom ? parseGuideRows(vars[b.rowsFrom]).map((r) => Array.from({ length: b.head.length }, (_, i) => r[i] ?? '')) : [];
function fillBlock(b, vars) {
    if (b.type !== 'table')
        return { ...b, text: fillVars(b.text, vars) };
    const { rowsFrom: _from, ...rest } = b;
    return { ...rest, head: b.head.map((t) => fillVars(t, vars)), rows: [...b.rows.map((r) => r.map((t) => fillVars(t, vars))), ...generatedRows(b, vars)] };
}
/** The article with its placeholders filled. */
export const fillArticle = (a, vars) => ({ ...a, title: fillVars(a.title, vars), summary: fillVars(a.summary, vars), body: a.body.map((b) => fillBlock(b, vars)) });
/** Every string a reader sees in an article, for search and the content checks. */
export function articleTexts(a) {
    const out = [a.title, a.summary];
    for (const b of a.body) {
        if (b.type === 'table')
            out.push(...b.head, ...b.rows.flat());
        else
            out.push(b.text);
    }
    return out;
}
/** `{name}` placeholders used in a text. */
export const placeholdersIn = (text) => [...text.matchAll(/\{([a-zA-Z0-9]+)\}/g)].map((m) => m[1]);
/** `[[screenshot: caption]]` or `[[screenshot: file | caption]]`. */
const SCREENSHOT_RE = /^\[\[screenshot:\s*(?:([^|\]]+?)\s*\|\s*)?(.+?)\s*\]\]$/;
const SCREENSHOT_ALL_RE = /\[\[screenshot:\s*(?:([^|\]]+?)\s*\|\s*)?(.+?)\s*\]\]/g;
/**
 * Where the handbook's pictures are served, under the web app's origin (apps/web/public/staff-guide,
 * static assets of the frontend Worker).
 */
export const GUIDE_SHOT_PATH = '/staff-guide/';
/** Picture file names an article's slots name (`[[screenshot: file | caption]]`), in order. */
export const guideShotFiles = (a) => a.body.flatMap((b) => (b.type === 'text' ? [...b.text.matchAll(SCREENSHOT_ALL_RE)].flatMap((m) => (m[1] ? [m[1]] : [])) : []));
/**
 * The article with each slot's file name made an absolute URL on the web app's origin
 * (`https://app.example.com/staff-guide/<file>`), so Platform Admin and the CRM load the same picture.
 */
export function guideShotUrls(a, webOrigin) {
    const base = webOrigin.replace(/\/+$/, '') + GUIDE_SHOT_PATH;
    const fix = (text) => text.replace(SCREENSHOT_ALL_RE, (all, file, caption) => (file && !/^[a-z]+:\/\//i.test(file) ? `[[screenshot: ${base}${file} | ${caption}]]` : all));
    return { ...a, body: a.body.map((b) => (b.type === 'text' ? { ...b, text: fix(b.text) } : b)) };
}
/** A text block's paragraphs (GuideBlock `text`). */
export function guideParts(text) {
    const out = [];
    for (const para of text.split(/\n\s*\n/)) {
        const lines = para
            .split('\n')
            .map((l) => l.trim())
            .filter(Boolean);
        if (!lines.length)
            continue;
        const shot = lines.length === 1 ? SCREENSHOT_RE.exec(lines[0]) : null;
        if (shot)
            out.push(shot[1] ? { type: 'screenshot', caption: shot[2], src: shot[1] } : { type: 'screenshot', caption: shot[2] });
        else if (lines.every((l) => l.startsWith('- ')))
            out.push({ type: 'list', items: lines.map((l) => l.slice(2)) });
        else if (lines.every((l) => /^\d+\.\s/.test(l)))
            out.push({ type: 'steps', items: lines.map((l) => l.replace(/^\d+\.\s+/, '')) });
        else if (lines[0].startsWith('### ')) {
            out.push({ type: 'h', text: lines[0].slice(4) });
            if (lines.length > 1)
                out.push(...guideParts(lines.slice(1).join('\n')));
        }
        else
            out.push({ type: 'p', text: lines.join(' ') });
    }
    return out;
}
/** Inline marks in a paragraph: `**bold**`, `*italic*`, `` `code` ``, `[[see: id]]`. */
export function guideInline(text) {
    const out = [];
    const re = /\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`|\[\[see:\s*([a-z0-9-]+)\s*\]\]/g;
    let at = 0;
    for (const m of text.matchAll(re)) {
        if (m.index > at)
            out.push({ type: 'text', text: text.slice(at, m.index) });
        if (m[1] !== undefined)
            out.push({ type: 'bold', text: m[1] });
        else if (m[2] !== undefined)
            out.push({ type: 'italic', text: m[2] });
        else if (m[3] !== undefined)
            out.push({ type: 'code', text: m[3] });
        else
            out.push({ type: 'see', id: m[4] });
        at = m.index + m[0].length;
    }
    if (at < text.length)
        out.push({ type: 'text', text: text.slice(at) });
    return out;
}
/** Article ids an article links to with `[[see: id]]`. */
export const seeLinks = (a) => articleTexts(a).flatMap((t) => [...t.matchAll(/\[\[see:\s*([a-z0-9-]+)\s*\]\]/g)].map((m) => m[1]));
//# sourceMappingURL=index.js.map